'use client';

import { useMemoizedFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgentLineGenerateVideo } from '@/app/components/icons/AgentLineGenerateVideo';
import { BaseLineInfo } from '@/app/components/icons/BaseLineInfo';
import { ProductLineElement } from '@/app/components/icons/ProductLineElement';
import { ProductLinePic } from '@/app/components/icons/ProductLinePic';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { MediaDropZone } from '@/app/components/shared/MediaUploader/MediaDropZone';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { toast } from '@/app/components/ui/use-toast';
import {
  CHARACTER_IMAGE_EXAMPLE_ONE,
  CHARACTER_IMAGE_EXAMPLE_TWO,
  MOTION_VIDEO_EXAMPLE_ONE,
  MOTION_VIDEO_EXAMPLE_TWO,
  SITE,
  UPLOAD_SCENE,
} from '@/app/constants';
import { useDevice, useFetch, useFetchRecreateData, useGlobalModal } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type {
  CreationWithTask,
  ElementType,
  FormImagePrompt,
  ImageUploadFile,
  TaskInfo,
  ToolTaskFormVideoPrompt,
  VideoUploadFile,
} from '@/app/types';
import {
  getTodayDateString,
  isExtendTask,
  isVideoDurationWithinRange,
  normalizeDiscreteVideoDurationToSeconds,
} from '@/app/utils';
import { safeStorage } from '@/app/utils/client';
import { ElementsDialog, type ElementsDialogState } from '../../_components/ElementsDialog';
import { CreationSelectorDialog } from '../_components/CreationSelectorDialog';
import { ExampleMediaPreview } from '../_components/ExampleMediaPreview';
import { ToolFormContainer } from '../_components/ToolFormContainer';
import { ToolFormSubmitButton } from '../_components/ToolFormSubmitButton';
import { ToolMediaUpload } from '../_components/ToolMediaUpload';
import { ToolMediaUploadHeader } from '../_components/ToolMediaUploadHeader';
import { SwitchOptionSetting } from '../_components/ToolSettingsOptions';
import { useCreationConsent } from '../_hooks/use-creation-consent';
import { useRunTempUploadBatch } from '../_hooks/use-run-temp-upload-batch';
import { useToolForm } from '../_hooks/use-tool-form';
import {
  applyToolFormParamsPatch,
  buildToolTaskRecreatePatch,
  createImagePromptFromCreation,
  createPromptListHelpers,
  createToolImagePromptFromElement,
  createToolImagePromptFromUploadFile,
  createToolVideoPromptFromUploadFile,
  createVideoPromptFromCreation,
} from '../_utils';
import { getToolTempImagesPendingItems, getToolTempVideosPendingItems } from '../_utils/tool-temp-upload-items';
import { materialsCreateMotion } from '../apis';
import { MotionLibrarySelectorDialog } from './_components/MotionLibrarySelectorDialog';
import type { MotionLibraryItem } from './types';

const maxVideoSize = 200 * 1024 * 1024;
const maxImageFileSize = 10 * 1024 * 1024;
const maxVideoDuration = 30;
const minVideoDuration = 2;
const minImageEdge = 300;
const maxImageEdge = 65536;
const minVideoEdge = 200;
const maxVideoEdge = 2048;
const minImageAspectRatio = 1 / 2.5;
const maxImageAspectRatio = 2.5;
const minVideoAspectRatio = 1 / 3;
const maxVideoAspectRatio = 3;
const ignoredCreateMotionErrorReasons = new Set(['MotionIgnore']);
const motionLibraryAutoOpenDateKey = 'motion_control_library_auto_open_date';

const isPersistedUri = (uri?: string): uri is string => !!uri && !uri.startsWith('blob:');

type MediaResolutionError = 'dimension' | 'ratio';
type MediaValidationLimits = {
  minEdge: number;
  maxEdge: number;
  minAspectRatio: number;
  maxAspectRatio: number;
  minRatioLabel: string;
  maxRatioLabel: string;
};

const imageValidationLimits: MediaValidationLimits = {
  minEdge: minImageEdge,
  maxEdge: maxImageEdge,
  minAspectRatio: minImageAspectRatio,
  maxAspectRatio: maxImageAspectRatio,
  minRatioLabel: '1:2.5',
  maxRatioLabel: '2.5:1',
};

const videoValidationLimits: MediaValidationLimits = {
  minEdge: minVideoEdge,
  maxEdge: maxVideoEdge,
  minAspectRatio: minVideoAspectRatio,
  maxAspectRatio: maxVideoAspectRatio,
  minRatioLabel: '1:3',
  maxRatioLabel: '3:1',
};

const getMediaResolutionError = (
  { width, height }: { width?: number; height?: number },
  { minEdge, maxEdge, minAspectRatio, maxAspectRatio }: MediaValidationLimits,
): MediaResolutionError | null => {
  if (!width || !height || width < minEdge || height < minEdge || width > maxEdge || height > maxEdge) {
    return 'dimension';
  }

  const aspectRatio = width / height;
  if (aspectRatio < minAspectRatio || aspectRatio > maxAspectRatio) {
    return 'ratio';
  }

  return null;
};

const hasKnownMediaResolution = (resolution?: { width?: number; height?: number }): boolean => {
  return !!resolution?.width && !!resolution.height;
};

const isUserUploadedMotionVideo = (videoPrompt?: ToolTaskFormVideoPrompt) => {
  if (!videoPrompt) return true;

  if (videoPrompt.motionLibrarySource) {
    return false;
  }

  if (videoPrompt.reference?.creation_id || videoPrompt.reference?.motion_id) {
    return false;
  }

  return true;
};

const MotionControlForm: FC = () => {
  const t = useTranslations();
  const { isMobile } = useDevice();
  const storeApi = useStoreApi();

  const { settings, updateSettings } = useToolForm('motion_control');
  const motionControlSettings = settings as typeof settings & { use_video_background?: boolean };
  const useVideoBackground = motionControlSettings.use_video_background ?? false;

  const [openMotionLibrary, setOpenMotionLibrary] = useState(false);
  const [openMaterialGuide, setOpenMaterialGuide] = useState(false);
  const [openVideoSelector, setOpenVideoSelector] = useState(false);
  const [openImageSelector, setOpenImageSelector] = useState(false);
  const [elementsDialogState, setElementsDialogState] = useState<ElementsDialogState>({ type: 'none' });
  const [selectedVideoCreationIndex, setSelectedVideoCreationIndex] = useState<number | null>(null);
  const [selectedImageCreationIndex, setSelectedImageCreationIndex] = useState<number | null>(null);
  const [selectedVideoCreation, setSelectedVideoCreation] = useState<CreationWithTask | undefined>();
  const [selectedImageCreation, setSelectedImageCreation] = useState<CreationWithTask | undefined>();
  const [isImageCreationConfirming, setIsImageCreationConfirming] = useState(false);
  const [isSubjectImageValidating, setIsSubjectImageValidating] = useState(false);
  const subjectSelectionRequestIdRef = useRef(0);

  const { open: openSimpleCreationDetailModal } = useGlobalModal('simpleCreationDetailModal');

  const { updateMotionControlFormParams, imagePrompts, videoPrompts, userInfo } = useStore((state) => ({
    updateMotionControlFormParams: state.updateMotionControlFormParams,
    imagePrompts: state.motionControlFormParams.imagePrompts,
    videoPrompts: state.motionControlFormParams.videoPrompts,
    userInfo: state.userInfo,
  }));

  const videoPrompt = videoPrompts?.[0] || null;
  const imagePrompt = imagePrompts?.[0] || null;
  const ensureFaceConsent = useCreationConsent(userInfo?.id, { requireFaceConsent: SITE === 'china' });

  useEffect(() => {
    if (videoPrompt) return;

    const today = getTodayDateString();
    if (safeStorage.getItem(motionLibraryAutoOpenDateKey) === today) return;

    safeStorage.setItem(motionLibraryAutoOpenDateKey, today);
    setOpenMotionLibrary(true);
  }, [videoPrompt]);

  const showMotionMediaError = useCallback((message: string) => {
    toast({
      message,
      type: 'error',
    });
  }, []);

  const getImageResolutionShortErrorMessage = useCallback(
    (imageResolution: { width?: number; height?: number }) => {
      const error = getMediaResolutionError(imageResolution, imageValidationLimits);
      if (error === 'dimension')
        return t('tools.motionControl.error.invalidImageDimension', {
          min: imageValidationLimits.minEdge,
          max: imageValidationLimits.maxEdge,
        });
      if (error === 'ratio')
        return t('tools.motionControl.error.invalidImageRatio', {
          minRatio: imageValidationLimits.minRatioLabel,
          maxRatio: imageValidationLimits.maxRatioLabel,
        });
      return null;
    },
    [t],
  );

  const getVideoResolutionShortErrorMessage = useCallback(
    (videoResolution: { width?: number; height?: number }) => {
      const error = getMediaResolutionError(videoResolution, videoValidationLimits);
      if (error === 'dimension')
        return t('tools.motionControl.error.invalidVideoDimension', {
          min: videoValidationLimits.minEdge,
          max: videoValidationLimits.maxEdge,
        });
      if (error === 'ratio')
        return t('tools.motionControl.error.invalidVideoRatio', {
          minRatio: videoValidationLimits.minRatioLabel,
          maxRatio: videoValidationLimits.maxRatioLabel,
        });
      return null;
    },
    [t],
  );

  const validateImageResolution = useCallback(
    (imageResolution: { width: number; height: number }) => {
      const errorMessage = getImageResolutionShortErrorMessage(imageResolution);
      if (errorMessage) {
        return {
          errorMessage,
          errorReason: 'invalidMotionControlImageResolution',
        };
      }

      return null;
    },
    [getImageResolutionShortErrorMessage],
  );

  const validateVideoMetadata = useCallback(
    ({ width, height }: { duration: number; aspectRatio: number; width: number; height: number }) => {
      return getVideoResolutionShortErrorMessage({ width, height });
    },
    [getVideoResolutionShortErrorMessage],
  );

  const { run: createMotion } = useFetch(
    (motionPrompt: ToolTaskFormVideoPrompt) =>
      materialsCreateMotion({
        body: {
          name: t('tools.motionControl.motionLibrary.defaultMotionName', { timestamp: Date.now() }),
          uri: motionPrompt.prompt?.video,
          cover_uri: isPersistedUri(motionPrompt.prompt?.coverImage) ? motionPrompt.prompt.coverImage : undefined,
          duration: motionPrompt.prompt?.duration,
        },
      }),
    {
      manual: true,
      onSuccess: ({ error }) => {
        if (error) {
          if (ignoredCreateMotionErrorReasons.has(error.reason)) {
            return;
          }

          toast({
            message:
              error.reason === 'MotionTotalLimitExceeded'
                ? t('tools.motionControl.motionLibrary.totalLimitExceeded')
                : error.message || t('popup.toast.networkError'),
            type: 'error',
          });
          return;
        }

        toast({
          message: t('tools.motionControl.motionSavedToast'),
          type: 'success',
        });
      },
      onError: () => {
        toast({
          message: t('popup.toast.networkError'),
          type: 'error',
        });
      },
    },
  );

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp images',
    (refs) =>
      getToolTempImagesPendingItems(
        refs,
        createPromptListHelpers(
          () => storeApi.getState().motionControlFormParams.imagePrompts,
          (prompts) => updateMotionControlFormParams('imagePrompts', prompts.filter(Boolean) as FormImagePrompt[]),
        ),
        { imagePrompts },
      ),
    [imagePrompts, updateMotionControlFormParams, userInfo],
  );

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload temp videos',
    (refs) =>
      getToolTempVideosPendingItems(
        refs,
        createPromptListHelpers(
          () => storeApi.getState().motionControlFormParams.videoPrompts,
          (prompts) =>
            updateMotionControlFormParams('videoPrompts', prompts.filter(Boolean) as ToolTaskFormVideoPrompt[]),
        ),
        { videoPrompts },
        UPLOAD_SCENE.MOTION,
      ),
    [updateMotionControlFormParams, userInfo, videoPrompts],
  );

  const handleTaskSuccess = useCallback(
    async (task: TaskInfo, inspirationId?: string, source?: string) => {
      const patch = await buildToolTaskRecreatePatch({
        tool: 'motion_control',
        task,
        inspirationId,
        inspirationFrom: source,
      });
      applyToolFormParamsPatch(updateMotionControlFormParams, patch);
      isUserUploadedVideoRef.current = isUserUploadedMotionVideo(patch.videoPrompts?.[0]);
    },
    [updateMotionControlFormParams],
  );

  useFetchRecreateData({
    onTaskSuccess: handleTaskSuccess,
  });

  const handleSelectVideo = useCallback((index: number | null, creation?: CreationWithTask) => {
    if (!creation) {
      setSelectedVideoCreationIndex(null);
      setSelectedVideoCreation(undefined);
      return;
    }

    setSelectedVideoCreationIndex(index);
    setSelectedVideoCreation(creation);
  }, []);

  const handleSelectImage = useCallback((index: number | null, creation?: CreationWithTask) => {
    if (!creation) {
      setSelectedImageCreationIndex(null);
      setSelectedImageCreation(undefined);
      return;
    }

    setSelectedImageCreationIndex(index);
    setSelectedImageCreation(creation);
  }, []);

  const handleRemoveVideo = useCallback(() => {
    updateMotionControlFormParams('videoPrompts', []);
  }, [updateMotionControlFormParams]);

  const cancelPendingSubjectImageValidation = useCallback(() => {
    subjectSelectionRequestIdRef.current += 1;
    setIsSubjectImageValidating(false);
  }, []);

  const handleRemoveImage = useCallback(() => {
    cancelPendingSubjectImageValidation();
    updateMotionControlFormParams('imagePrompts', []);
  }, [cancelPendingSubjectImageValidation, updateMotionControlFormParams]);

  const handleVideoUpload = useMemoizedFn((file: VideoUploadFile) => {
    isUserUploadedVideoRef.current = true;
    const videoPrompt = createToolVideoPromptFromUploadFile(file);
    updateMotionControlFormParams('videoPrompts', [videoPrompt]);
    updateSettings({ duration: normalizeDiscreteVideoDurationToSeconds(file.file?.duration) });

    if (file.status === 'error' || file.status === 'removed') {
      updateMotionControlFormParams('videoPrompts', []);
    }
  });

  const handleImageUpload = useMemoizedFn((file: ImageUploadFile) => {
    cancelPendingSubjectImageValidation();
    const imagePrompt = createToolImagePromptFromUploadFile(file);
    updateMotionControlFormParams('imagePrompts', [imagePrompt]);

    if (file.status === 'error' || file.status === 'removed') {
      updateMotionControlFormParams('imagePrompts', []);
    }
  });

  // Track whether the video was uploaded by the user (not selected from motion library)
  const isUserUploadedVideoRef = useRef(true);

  const handleMotionLibrarySelect = useCallback(
    (item: MotionLibraryItem) => {
      isUserUploadedVideoRef.current = false;
      updateMotionControlFormParams('videoPrompts', [
        {
          id: item.id,
          status: 'done',
          prompt: {
            video: item.videoUrl,
            videoPreview: item.videoUrl,
            coverImage: item.thumbnail,
            coverImagePreview: item.thumbnail,
            duration: item.duration,
          },
          reference: { motion_id: item.id },
          motionLibrarySource: item.source,
        },
      ]);
      updateSettings({ duration: normalizeDiscreteVideoDurationToSeconds(item.duration) });
    },
    [updateMotionControlFormParams, updateSettings],
  );

  const handleSubmitSuccess = useCallback(() => {
    setOpenMotionLibrary(false);
    setElementsDialogState({ type: 'none' });
    setOpenVideoSelector(false);
    setOpenImageSelector(false);

    const latestVideoPrompt = storeApi.getState().motionControlFormParams.videoPrompts?.[0];

    if (
      isUserUploadedVideoRef.current &&
      isUserUploadedMotionVideo(latestVideoPrompt) &&
      latestVideoPrompt?.prompt &&
      isPersistedUri(latestVideoPrompt.prompt.video)
    ) {
      createMotion(latestVideoPrompt);
    }
    return undefined;
  }, [createMotion, storeApi]);

  const handleBeforeSubmit = useCallback(() => {
    setElementsDialogState({ type: 'none' });
    setOpenVideoSelector(false);
    setOpenImageSelector(false);
    return ensureFaceConsent();
  }, [ensureFaceConsent]);

  const handleMotionLibraryOpenChange = useCallback((nextOpen: boolean) => {
    setOpenMotionLibrary(nextOpen);
    if (nextOpen) {
      setElementsDialogState({ type: 'none' });
    }
  }, []);

  const handleOpenMotionLibrary = useCallback(() => {
    setElementsDialogState({ type: 'none' });
    setOpenMotionLibrary(true);
  }, []);

  const handleElementsDialogStateChange = useCallback((nextState: ElementsDialogState) => {
    setElementsDialogState(nextState);
    if (nextState.type !== 'none') {
      setOpenMotionLibrary(false);
    }
  }, []);

  const handleOpenSubjectDialog = useCallback(() => {
    setOpenMotionLibrary(false);
    setElementsDialogState({ type: 'elementsSelector', allowedModalities: ['image'] });
  }, []);

  const getSelectedImage = useCallback(async (url: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const img = new Image();
      const objectUrl = URL.createObjectURL(blob);

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = objectUrl;
      });
      URL.revokeObjectURL(objectUrl);
      return { blob, width: img.width, height: img.height };
    } catch {
      return null;
    }
  }, []);

  const getImageResolutionFromUrl = useCallback(async (url?: string) => {
    if (!url) return null;

    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const img = new Image();
      const objectUrl = URL.createObjectURL(blob);

      try {
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = objectUrl;
        });
        return { width: img.width, height: img.height };
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    } catch {
      return null;
    }
  }, []);

  const validateSelectedImagePrompt = useCallback(
    async (prompt: FormImagePrompt | null) => {
      const promptWithResolution = prompt?.prompt as
        | (FormImagePrompt['prompt'] & { resolution?: { width?: number; height?: number } })
        | null
        | undefined;
      const resolution =
        promptWithResolution?.selectedImageWidth && promptWithResolution?.selectedImageHeight
          ? { width: promptWithResolution.selectedImageWidth, height: promptWithResolution.selectedImageHeight }
          : promptWithResolution?.sourceImageWidth && promptWithResolution?.sourceImageHeight
            ? { width: promptWithResolution.sourceImageWidth, height: promptWithResolution.sourceImageHeight }
            : promptWithResolution?.resolution?.width && promptWithResolution?.resolution?.height
              ? { width: promptWithResolution.resolution.width, height: promptWithResolution.resolution.height }
              : await getImageResolutionFromUrl(
                  promptWithResolution?.selectedImagePreview ||
                    promptWithResolution?.selectedImage ||
                    promptWithResolution?.sourceImagePreview ||
                    promptWithResolution?.sourceImage,
                );

      const errorMessage = resolution
        ? getImageResolutionShortErrorMessage(resolution)
        : t('tools.motionControl.error.invalidImageDimension', {
            min: imageValidationLimits.minEdge,
            max: imageValidationLimits.maxEdge,
          });
      if (errorMessage) {
        showMotionMediaError(errorMessage);
        return false;
      }

      return true;
    },
    [getImageResolutionFromUrl, getImageResolutionShortErrorMessage, showMotionMediaError, t],
  );

  const validateSelectedVideoCreation = useCallback(
    (creation: CreationWithTask) => {
      const errorMessage = getVideoResolutionShortErrorMessage(creation.resolution);
      if (errorMessage) {
        showMotionMediaError(errorMessage);
        return false;
      }

      return true;
    },
    [getVideoResolutionShortErrorMessage, showMotionMediaError],
  );

  const handleSubjectSelect = useCallback(
    async (elements: ElementType[]) => {
      setElementsDialogState({ type: 'none' });
      subjectSelectionRequestIdRef.current += 1;
      const requestId = subjectSelectionRequestIdRef.current;

      const element = elements.find((item) => item.modality === 'image');
      const imagePrompt = element ? createToolImagePromptFromElement(element) : null;

      if (!imagePrompt) {
        return;
      }

      updateMotionControlFormParams('imagePrompts', [imagePrompt]);
      setIsSubjectImageValidating(true);

      try {
        if (!(await validateSelectedImagePrompt(imagePrompt))) {
          if (subjectSelectionRequestIdRef.current !== requestId) {
            return;
          }

          updateMotionControlFormParams('imagePrompts', []);
        }
      } finally {
        if (subjectSelectionRequestIdRef.current === requestId) {
          setIsSubjectImageValidating(false);
        }
      }
    },
    [updateMotionControlFormParams, validateSelectedImagePrompt],
  );

  const hasUploadingMedia =
    imagePrompts.some((prompt) => prompt.status === 'uploading') ||
    videoPrompts.some((prompt) => prompt.status === 'uploading');
  const submitDisabledTip = useMemo(() => {
    if (hasUploadingMedia || isSubjectImageValidating) return t('create.createForm.error.uploadingMedia');
    if (!videoPrompt || videoPrompt.status !== 'done') return t('tools.motionControl.error.disabledMissingMotionVideo');
    if (!imagePrompt || imagePrompt.status !== 'done') return t('tools.motionControl.error.disabledMissingTargetImage');
    return undefined;
  }, [hasUploadingMedia, imagePrompt, isSubjectImageValidating, t, videoPrompt]);
  const isSubmitDisabled = Boolean(submitDisabledTip);

  const remainingVideoSlots = videoPrompt ? 0 : 1;
  const remainingImageSlots = imagePrompt ? 0 : 1;
  const emptyExtraActionClassName =
    'group flex w-full cursor-pointer items-center justify-center gap-2 rounded-8 px-5 py-4 transition-colors hover:bg-system-white/5';

  return (
    <>
      <ToolFormContainer
        title={t('tools.motionControl.title')}
        description={t('tools.motionControl.description')}
        exampleSection={<ExampleMediaPreview tool="motion_control" />}
        className="px-7 pb-5 sm:px-4"
        submitButtonClassName="px-7 sm:px-4"
        mediaSection={
          <MediaDropZone
            acceptVideo={true}
            maxVideoCount={remainingVideoSlots}
            maxVideoSize={maxVideoSize}
            maxVideoDuration={maxVideoDuration}
            minVideoDuration={minVideoDuration}
            validateVideoMetadata={validateVideoMetadata}
            videoScene={UPLOAD_SCENE.MOTION}
            maxImageCount={remainingImageSlots}
            disabled={remainingVideoSlots === 0 && remainingImageSlots === 0}
            maxImageFileSize={maxImageFileSize}
            validateImageResolution={validateImageResolution}
            getSelectedImage={getSelectedImage}
            allowTempMode={true}
            enablePaste={true}
            onChange={(file, type) => {
              if (type === 'video') handleVideoUpload(file as VideoUploadFile);
              else if (type === 'image') handleImageUpload(file as ImageUploadFile);
            }}
          >
            <div className="grid w-full grid-cols-2 gap-5 sm:grid-cols-1">
              <div className="flex flex-col">
                <ToolMediaUpload
                  type="video"
                  videoScene={UPLOAD_SCENE.MOTION}
                  header={
                    <ToolMediaUploadHeader
                      icon={<AgentLineGenerateVideo className="size-5 text-system-white" />}
                      title={t('tools.motionControl.motionVideo')}
                    />
                  }
                  videoPrompt={videoPrompt}
                  isMobile={isMobile}
                  surfaceVariant="compact"
                  maxVideoSize={maxVideoSize}
                  maxDuration={maxVideoDuration}
                  minDuration={minVideoDuration}
                  validateVideoMetadata={validateVideoMetadata}
                  onVideoUpload={handleVideoUpload}
                  onOpenSelector={() => setOpenVideoSelector(true)}
                  onRemoveMedia={handleRemoveVideo}
                  emptyActionsLayout="stackedExtra"
                  emptyExtraAction={
                    <button type="button" onClick={handleOpenMotionLibrary} className={emptyExtraActionClassName}>
                      <AgentLineGenerateVideo className="size-4 text-system-white" />
                      <span className="text-system-white/40 text-xs leading-[1.4] group-hover:text-system-white">
                        {t('tools.motionControl.motionLibrary.title')}
                      </span>
                    </button>
                  }
                />
              </div>
              <div className="flex flex-col">
                <ToolMediaUpload
                  type="image"
                  header={
                    <ToolMediaUploadHeader
                      icon={<ProductLinePic className="size-[22px] text-system-white" />}
                      title={t('tools.motionControl.targetImage')}
                    />
                  }
                  imagePrompt={imagePrompt}
                  isMobile={isMobile}
                  surfaceVariant="compact"
                  maxFileSize={maxImageFileSize}
                  validateImageResolution={validateImageResolution}
                  getSelectedImage={getSelectedImage}
                  onImageUpload={handleImageUpload}
                  onOpenSelector={() => setOpenImageSelector(true)}
                  onRemoveMedia={handleRemoveImage}
                  emptyActionsLayout="stackedExtra"
                  emptyExtraAction={
                    <button type="button" onClick={handleOpenSubjectDialog} className={emptyExtraActionClassName}>
                      <ProductLineElement className="size-4 text-system-white" />
                      <span className="text-system-white/40 text-xs leading-[1.4] group-hover:text-system-white">
                        {t('create.char2VideoForm.element.addElements')}
                      </span>
                    </button>
                  }
                />
              </div>
            </div>
            <div className="mt-2 w-full text-left text-system-white/40 text-xs leading-[1.4]">
              {t.rich('tools.motionControl.materialGuide.tip', {
                link: (chunks) => (
                  <span
                    onClick={() => setOpenMaterialGuide(true)}
                    className="cursor-pointer text-system-white/60 underline underline-offset-2 transition-colors hover:text-system-white/40"
                  >
                    {chunks}
                  </span>
                ),
              })}
            </div>
          </MediaDropZone>
        }
        submitButton={
          <ToolFormSubmitButton
            taskType="motion_control"
            className="h-12 rounded-12 font-semibold text-lg"
            disabled={isSubmitDisabled}
            disabledTip={submitDisabledTip}
            onBeforeSubmit={handleBeforeSubmit}
            onSubmitSuccess={handleSubmitSuccess}
          >
            {t('create.createForm.submit')}
          </ToolFormSubmitButton>
        }
      >
        <div className="flex w-full flex-col gap-5">
          <SwitchOptionSetting
            className="rounded-12 border border-system-white08 bg-system-white02 px-4 py-3"
            value={useVideoBackground}
            onChange={(value) =>
              updateSettings({ use_video_background: value } as Parameters<typeof updateSettings>[0])
            }
            title={
              <span className="inline-flex items-center gap-1.5">
                {t('tools.motionControl.settings.useVideoBackground')}
                <CustomTooltip content={t('tools.motionControl.settings.useVideoBackgroundTip')} side="top">
                  <span className="inline-flex cursor-help text-system-white48 hover:text-system-white">
                    <BaseLineInfo className="size-4" />
                  </span>
                </CustomTooltip>
              </span>
            }
          />
        </div>
      </ToolFormContainer>

      <Dialog open={openMaterialGuide} onOpenChange={setOpenMaterialGuide}>
        <DialogContent className="max-w-[560px] bg-[#25282C] light:bg-system-white p-6 light:text-system-black text-system-white">
          <DialogHeader className="gap-2">
            <DialogTitle className="light:text-system-black text-system-white">
              {t('tools.motionControl.materialGuide.title')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            <section>
              <h3 className="mb-2 font-medium text-sm">{t('tools.motionControl.materialGuide.motionVideoTitle')}</h3>
              <p className="mb-3 light:text-system-black/60 text-system-white/60 text-xs leading-[1.6]">
                {t('tools.motionControl.materialGuide.motionVideoDescription')}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex aspect-[16/9] items-center justify-center overflow-hidden rounded-8 bg-system-black/40 light:bg-system-black04">
                  <video
                    src={MOTION_VIDEO_EXAMPLE_ONE}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex aspect-[16/9] items-center justify-center overflow-hidden rounded-8 bg-system-black/40 light:bg-system-black04">
                  <video
                    src={MOTION_VIDEO_EXAMPLE_TWO}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
            </section>

            <section>
              <h3 className="mb-2 font-medium text-sm">{t('tools.motionControl.materialGuide.characterImageTitle')}</h3>
              <p className="mb-3 light:text-system-black/60 text-system-white/60 text-xs leading-[1.6]">
                {t('tools.motionControl.materialGuide.characterImageDescription')}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex aspect-[16/9] items-center justify-center overflow-hidden rounded-8 bg-system-black/40 light:bg-system-black04">
                  <img src={CHARACTER_IMAGE_EXAMPLE_ONE} alt="" className="h-full w-full object-cover" />
                </div>
                <div className="flex aspect-[16/9] items-center justify-center overflow-hidden rounded-8 bg-system-black/40 light:bg-system-black04">
                  <img src={CHARACTER_IMAGE_EXAMPLE_TWO} alt="" className="h-full w-full object-cover" />
                </div>
              </div>
            </section>
          </div>
        </DialogContent>
      </Dialog>

      <MotionLibrarySelectorDialog
        open={openMotionLibrary}
        onOpenChange={handleMotionLibraryOpenChange}
        onSelect={handleMotionLibrarySelect}
      />

      <ElementsDialog
        mode={isMobile ? 'sheet' : 'drawer'}
        taskType="character2video"
        dialogState={elementsDialogState}
        onDialogStateChange={handleElementsDialogStateChange}
        remainingSelections={imagePrompt ? 0 : 1}
        maxReferenceCount={1}
        disabledIds={[]}
        onElementsSelect={handleSubjectSelect}
        onElementsDelete={() => {}}
        onSubjectSave={() => {}}
      />

      <CreationSelectorDialog
        open={openVideoSelector}
        onOpenChange={setOpenVideoSelector}
        selectedIndex={selectedVideoCreationIndex}
        mediaType="video"
        maxDuration={maxVideoDuration}
        minDuration={minVideoDuration}
        onSelectionChange={handleSelectVideo}
        isCreationDisabled={(creation) => !!getVideoResolutionShortErrorMessage(creation.resolution)}
        getDisabledReason={(creation) => getVideoResolutionShortErrorMessage(creation.resolution)}
        onConfirm={() => {
          setOpenVideoSelector(false);
          const selectedCreation = selectedVideoCreation;
          const selectedDuration =
            (isExtendTask(selectedCreation?.task.type)
              ? selectedCreation?.duration
              : selectedCreation?.task.settings.duration) || 0;
          if (
            selectedCreation &&
            isVideoDurationWithinRange({
              duration: selectedDuration,
              minDuration: minVideoDuration,
              maxDuration: maxVideoDuration,
            })
          ) {
            if (!validateSelectedVideoCreation(selectedCreation)) {
              return;
            }

            const videoPrompt = createVideoPromptFromCreation(selectedCreation, {
              includeReference: true,
            });
            isUserUploadedVideoRef.current = false;
            updateMotionControlFormParams('videoPrompts', [videoPrompt]);
            updateSettings({ duration: normalizeDiscreteVideoDurationToSeconds(selectedDuration) });
          }
        }}
        onViewCreation={(creation) => {
          openSimpleCreationDetailModal({
            creation,
          });
        }}
      />

      <CreationSelectorDialog
        open={openImageSelector}
        onOpenChange={setOpenImageSelector}
        selectedIndex={selectedImageCreationIndex}
        mediaType="image"
        onSelectionChange={handleSelectImage}
        confirmLoading={isImageCreationConfirming}
        isCreationDisabled={(creation) =>
          hasKnownMediaResolution(creation.resolution) && !!getImageResolutionShortErrorMessage(creation.resolution)
        }
        getDisabledReason={(creation) =>
          hasKnownMediaResolution(creation.resolution) ? getImageResolutionShortErrorMessage(creation.resolution) : null
        }
        onConfirm={async () => {
          if (!selectedImageCreation) {
            setOpenImageSelector(false);
            return;
          }

          const nextImagePrompt = createImagePromptFromCreation(selectedImageCreation, { includeReference: true });
          setIsImageCreationConfirming(true);
          try {
            if (await validateSelectedImagePrompt(nextImagePrompt)) {
              updateMotionControlFormParams('imagePrompts', [nextImagePrompt]);
              setOpenImageSelector(false);
            }
          } finally {
            setIsImageCreationConfirming(false);
          }
        }}
        onViewCreation={(creation) => {
          openSimpleCreationDetailModal({
            creation,
          });
        }}
      />
    </>
  );
};

export default MotionControlForm;
